import { Container } from 'reactstrap';
import React, { Suspense, useState, useEffect } from "react";

const Topbar = React.lazy(() => import("./Topbar"));
const LeftSidebar = React.lazy(() => import("./LeftSidebar"));
const loading = () => <div className="text-center"/>;

interface DashboardLayoutProps {
  children?: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const [isMenuCollapsed, setIsMenuCollapsed] = useState<boolean>(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);

  /**
   * Toggle mobile menu
   */
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);

    if (!isMobileMenuOpen) {
      document.body.classList.add('sidebar-enable');
      showLeftSideBarBackdrop();
    } else {
      document.body.classList.remove('sidebar-enable');
      hideLeftSideBarBackdrop();
    }
  };

  /**
   * Show backdrop for mobile menu
   */
  const showLeftSideBarBackdrop = () => {
    const backdrop = document.createElement('div');
    backdrop.id = 'sidebar-backdrop';
    backdrop.className = 'sidebar-backdrop';
    backdrop.onclick = () => {
      setIsMobileMenuOpen(false);
      document.body.classList.remove('sidebar-enable');
      hideLeftSideBarBackdrop();
    };
    document.body.appendChild(backdrop);
  };

  /**
   * Hide backdrop for mobile menu
   */
  const hideLeftSideBarBackdrop = () => {
    const backdrop = document.getElementById('sidebar-backdrop');
    if (backdrop) {
      backdrop.remove();
    }
  };

  /**
   * Close mobile menu when clicking outside
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const sidebar = document.querySelector('.app-menu');
      const toggleButton = document.querySelector('.button-toggle-menu');

      if (isMobileMenuOpen &&
          sidebar &&
          !sidebar.contains(event.target as Node) &&
          toggleButton &&
          !toggleButton.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
        document.body.classList.remove('sidebar-enable');
        hideLeftSideBarBackdrop();
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  /**
   * Always keep sidebar expanded
   */
  useEffect(() => {
    document.body.classList.remove('sidebar-collapsed');
    document.documentElement.setAttribute('data-sidenav-size', 'default');

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('sidebar-collapsed', 'sidebar-enable');
      document.documentElement.removeAttribute('data-sidenav-size');
      hideLeftSideBarBackdrop();
    };
  }, []);

  return (
    <div
      id="wrapper"
      className={`dashboard-layout ${isMenuCollapsed ? 'menu-collapsed' : ''} ${isMobileMenuOpen ? 'mobile-menu-open' : ''}`}
    >
      <Suspense fallback={loading()}>
        <LeftSidebar
          isCondensed={isMenuCollapsed}
          hideLogo={false}
          isMobileOpen={isMobileMenuOpen}
          onExpandSidebar={() => setIsMenuCollapsed(false)}
        />
      </Suspense>

      <div className="content-page">
        <Suspense fallback={loading()}>
          <Topbar
            mobileMenuToggle={toggleMobileMenu}
            isMenuCollapsed={isMenuCollapsed}
            isMobileMenuOpen={isMobileMenuOpen}
          />
        </Suspense>

        <div className="content my-2">
          <Container fluid>
            <Suspense fallback={loading()}>
              {children}
            </Suspense>
          </Container>
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;
